import { useEffect, useRef, useState } from "react";
import { Smartphone } from "lucide-react";
import { BackToLoginButton } from "@/features/auth/components/LoginForm/Forms/FormParts/BackToLoginButton";
import { LoginFormError } from "@/features/auth/components/LoginForm/Forms/FormParts/LoginFormError";
import { SecurityStepHeader } from "@/features/auth/components/LoginForm/Forms/FormParts/SecurityStepHeader";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { APP_APPROVAL_CHANNEL } from "@/features/auth/types/auth.type";
import { ONBOARDING_COPY } from "@/features/legal/config/onboarding.config";
import { ButtonWithLoader } from "@/shared/components/customButtons/ButtonWithSpinner";

export const LOGIN_APPROVAL_POLL_INTERVAL_MS = 2000;

const APP_APPROVAL_COPY = {
  TITLE: "Validez dans l'application Yeati",
  DESCRIPTION: "Nous avons envoyé une demande à votre téléphone. Ouvrez l'application Yeati et choisissez ce nombre :",
  WAITING: "En attente de votre validation…",
  NO_NOTIFICATION: "Pas de notification ? Ouvrez simplement l'application Yeati.",
  SMS_FALLBACK: "Recevoir un code par SMS",
} as const;

type AppApprovalFormProps = { onBackToLogin: () => void };

// The web side of a sign-in approved in the Yeati app (yeati-api,
// docs/development/app-login-approval.md): the number to pick in the app, a poll of the request
// every 2 seconds, and the SMS code always one click away.
export const AppApprovalForm = ({ onBackToLogin }: AppApprovalFormProps) => {
  const {
    isTwoFactorRequired,
    twoFactorData: { channel, approval },
    error,
    pollLoginApproval,
    fallbackLoginApprovalToSms,
  } = useAuth();
  const [isWaiting, setIsWaiting] = useState(true);
  const [isSwitchingToSms, setIsSwitchingToSms] = useState(false);
  const pollRef = useRef(pollLoginApproval);
  pollRef.current = pollLoginApproval;

  const isActive = isTwoFactorRequired && channel === APP_APPROVAL_CHANNEL && !!approval;

  // One poll at a time, the next one 2 seconds after the previous answer.
  useEffect(() => {
    if (!isActive) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setIsWaiting(true);

    const poll = async () => {
      const stillPending = await pollRef.current().catch(() => true);
      if (cancelled) return;
      if (stillPending) timer = setTimeout(() => void poll(), LOGIN_APPROVAL_POLL_INTERVAL_MS);
      else setIsWaiting(false);
    };
    timer = setTimeout(() => void poll(), LOGIN_APPROVAL_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isActive, approval?.pollSecret]);

  if (!isActive) return null;

  const switchToSms = async () => {
    setIsSwitchingToSms(true);
    try {
      await fallbackLoginApprovalToSms();
    }
    finally {
      setIsSwitchingToSms(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="app-approval-form">
      <SecurityStepHeader title={APP_APPROVAL_COPY.TITLE} description={APP_APPROVAL_COPY.DESCRIPTION} />
      <LoginFormError title={error.title} description={error.description} />

      <div className="flex flex-col items-center gap-3">
        <span
          className="text-6xl font-bold tracking-widest text-gray-900 dark:text-white"
          data-testid="app-approval-number"
          aria-label={`Nombre à choisir dans l'application : ${approval.number}`}
        >
          {approval.number}
        </span>
        {isWaiting && (
          <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" aria-live="polite">
            <Smartphone size={16} aria-hidden />
            {APP_APPROVAL_COPY.WAITING}
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-300">{APP_APPROVAL_COPY.NO_NOTIFICATION}</p>

      <ButtonWithLoader
        type="button"
        color="light"
        pill
        isLoading={isSwitchingToSms}
        onClick={() => void switchToSms()}
        data-testid="app-approval-sms-fallback"
      >
        {APP_APPROVAL_COPY.SMS_FALLBACK}
      </ButtonWithLoader>

      <BackToLoginButton onClick={onBackToLogin} />
      <p className="text-xs text-gray-500 dark:text-gray-400">{ONBOARDING_COPY.SECURITY_FOOTNOTE}</p>
    </div>
  );
};
